import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChartLegend,
  Widget,
  LineChart,
  Typography,
  Box,
  Select,
  IconTextButton,
} from '@scope/scope-one-ui/core';
import { LinearData } from '@scope/scope-one-ui/core/Charts/LineChart/LineChart.d';
import { COLORS } from '@scope/scope-one-ui/theme';
import Download from 'assets/icons/download.svg';
import { useGetExportUrl, useGetCarbonIntensity } from '../../services/climateStressTest';
import { PageData } from '../../types/climateStressTest';
import { Tooltip } from './TransitionRisk.styled';

export const CarbonIntensity: FC<PageData> = ({ entity, countries, sectors }) => {
  const { t } = useTranslation();

  const [sector, setSector] = useState(entity.sector.code);
  const [country, setCountry] = useState(entity.country.name);

  const countryIso = countries.find((item) => item.name === country)?.iso!;

  const { data: risk } = useGetCarbonIntensity(countryIso, sector);
  const exportFile = useGetExportUrl('carbon-intensity', countryIso, sector);

  const getRiskData = (key: string) =>
    (risk?.data || []).map((item) => ({ x: `${item.year}`, y: item[key] }));

  const chartData: LinearData[] = [
    { name: t('Orderly'), color: COLORS.primary.main, data: getRiskData('orderly') },
    { name: t('Disorderly'), color: COLORS.warning.main, data: getRiskData('disorderly') },
    { name: t('Hot house'), color: COLORS.error.main, data: getRiskData('hotHouse') },
  ];

  const legend = (
    <ChartLegend items={chartData.map(({ color, name }) => ({ color: color!, label: name }))} />
  );

  return (
    <Widget
      title={t('Carbon intensity')}
      tooltip={
        <Tooltip display="flex" flexDirection="column">
          <Typography variant="body1">
            <span>{t('x-axis: ')}</span>
            {t('years')}
          </Typography>
          <Typography variant="body1">
            <span>{t('y-axis: ')}</span>
            {t('kg of CO2 equivalents per EUR of revenue')}
          </Typography>
          {legend}
        </Tooltip>
      }
      customHeader={
        <IconTextButton icon={<Download />} onClick={exportFile}>
          Export
        </IconTextButton>
      }
    >
      <Box display="flex" flexDirection="column" p={2} gridGap={2} pb={4}>
        <Box display="flex" gridGap={2} justifyContent="flex-end">
          <Select
            value={sector}
            label={t('Sector')}
            onChange={(val) => setSector(val as string)}
            options={sectors.map(({ code, name }) => ({ value: code, label: name }))}
          />
          {!entity.isSovereign && (
            <Select
              value={country}
              label={t('Country')}
              onChange={(val) => setCountry(val as string)}
              options={countries.map(({ name }) => ({ value: name, label: name }))}
            />
          )}
        </Box>
        <LineChart label={t('CO2 (kg)/EUR')} data={chartData} xLabel={t('Date')} />
        {legend}
      </Box>
    </Widget>
  );
};
